/**
 * Central Web Audio sound bus for Celebrity Smash. All effects are synthesized
 * at runtime (no MP3 dependency) and respect the global `cs.sounds.muted`
 * localStorage preference set by SettingsMenu.
 *
 * Usage:
 *   import { playClick, playAffirm, playError, playTick } from "@/lib/sounds";
 *   <button onClick={() => { playClick(); doThing(); }} />
 */
const MUTE_KEY = "cs.sounds.muted";

let sharedCtx = null;
const getCtx = () => {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedCtx || sharedCtx.state === "closed") {
    try { sharedCtx = new Ctx(); } catch { return null; }
  }
  if (sharedCtx.state === "suspended") {
    // Will unlock after first user gesture; safe to call again.
    sharedCtx.resume().catch(() => {});
  }
  return sharedCtx;
};

const isMuted = () => {
  try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
};

export const setSoundsMuted = (muted) => {
  try { localStorage.setItem(MUTE_KEY, muted ? "1" : "0"); } catch {}
  window.dispatchEvent(new CustomEvent("cs:sounds-toggle", { detail: { muted } }));
};

export const getSoundsMuted = isMuted;

/** Envelope-shaped tone. `frequency` in Hz; `duration` in seconds. */
const tone = (frequency, { duration = 0.12, type = "sine", volume = 0.14,
                            attack = 0.005, release = 0.08, delay = 0 } = {}) => {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  const start = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(volume, start + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration + release);
  osc.connect(gain).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + release + 0.02);
};

/** Sharp UI click — for every button tap. */
export const playClick = () => tone(1400, { duration: 0.02, type: "square", volume: 0.08, release: 0.02 });

/** Soft "select" tick — for choosing a portrait or reason. */
export const playSelect = () => {
  tone(720, { duration: 0.05, type: "triangle", volume: 0.1, release: 0.04 });
  tone(1080, { duration: 0.05, type: "triangle", volume: 0.08, release: 0.04, delay: 0.03 });
};

/** Affirming chime — for locking in an answer or advancing a stage. */
export const playAffirm = () => {
  tone(660, { duration: 0.09, type: "sine", volume: 0.12, release: 0.09 });
  tone(880, { duration: 0.11, type: "sine", volume: 0.11, release: 0.11, delay: 0.05 });
  tone(1320, { duration: 0.16, type: "sine", volume: 0.09, release: 0.14, delay: 0.11 });
};

/** Error/no-go buzz — invalid actions. */
export const playError = () => {
  tone(220, { duration: 0.16, type: "sawtooth", volume: 0.11, release: 0.1 });
  tone(180, { duration: 0.22, type: "sawtooth", volume: 0.09, release: 0.14, delay: 0.06 });
};

/** Countdown tick — for the last 10s of the round timer. */
export const playTick = (critical = false) =>
  tone(critical ? 1200 : 880, { duration: 0.05, type: "square",
                                  volume: critical ? 0.14 : 0.1, release: 0.04 });

/** Success reveal — big win. */
export const playReveal = () => {
  tone(523, { duration: 0.09, type: "sine", volume: 0.1 });
  tone(659, { duration: 0.09, type: "sine", volume: 0.1, delay: 0.06 });
  tone(784, { duration: 0.09, type: "sine", volume: 0.1, delay: 0.12 });
  tone(1047, { duration: 0.28, type: "sine", volume: 0.14, release: 0.24, delay: 0.18 });
};
